"use client";

import { CheckIcon } from "lucide-react";
import { useEffect, useRef, useState } from "react";

import {
  concernsSummary,
  pacingLine,
  trackLabel,
} from "@/lib/onboarding/copy";
import { cn } from "@/lib/utils";

import type { StepProps } from "../types";
import { StepHeader } from "../ui";

/** Total time on the loader before it advances. */
const BUILD_DURATION_MS = 2200;
/** Gap between check lines appearing. */
const LINE_STAGGER_MS = 600;

export function BuildingStep({ state, advance }: StepProps) {
  const { track, exam_timeframe, concerns, weekly_scenario_goal } =
    state.answers;

  const focus = concernsSummary(concerns);
  const lines = [
    pacingLine(exam_timeframe || undefined),
    focus ? `Focus: ${focus}` : "Covering the whole exam",
    weekly_scenario_goal
      ? `${weekly_scenario_goal} scenarios a week`
      : "A steady daily habit",
  ];

  // Line 0 shows immediately; line i at i * LINE_STAGGER_MS.
  const [revealed, setRevealed] = useState(1);
  const [filled, setFilled] = useState(false);

  // Latest advance without restarting the timers when its identity changes.
  const advanceRef = useRef(advance);
  useEffect(() => {
    advanceRef.current = advance;
  }, [advance]);
  const advanced = useRef(false);

  useEffect(() => {
    const timers: ReturnType<typeof setTimeout>[] = [];
    for (let i = 1; i < lines.length; i++) {
      timers.push(setTimeout(() => setRevealed(i + 1), i * LINE_STAGGER_MS));
    }
    // Start the width transition two frames after mount: the browser must
    // compute the w-0 style first, or the bar jumps straight to full.
    let frame = requestAnimationFrame(() => {
      frame = requestAnimationFrame(() => setFilled(true));
    });
    timers.push(
      setTimeout(() => {
        if (advanced.current) return;
        advanced.current = true;
        advanceRef.current();
      }, BUILD_DURATION_MS),
    );
    return () => {
      timers.forEach(clearTimeout);
      cancelAnimationFrame(frame);
    };
  }, [lines.length]);

  return (
    <>
      <StepHeader
        kicker="Building"
        title={
          track ? `Building your ${trackLabel(track)} plan.` : "Building your plan."
        }
      />
      <div className="mt-16">
        <ul aria-live="polite" className="mt-8 flex flex-col gap-4">
          {lines.map((line, i) => {
            const visible = i < revealed;
            return (
              <li
                key={i}
                // Pending lines stay out of the accessibility tree so the
                // live region announces each line as it is revealed.
                aria-hidden={!visible}
                className={cn(
                  "flex items-center gap-3 text-base motion-safe:transition-colors",
                  visible ? "text-foreground" : "text-muted-foreground",
                )}
              >
                <CheckIcon
                  aria-hidden="true"
                  className={cn(
                    "size-5 shrink-0 motion-safe:transition-opacity",
                    visible
                      ? "text-emerald-600 dark:text-emerald-400"
                      : "opacity-30",
                  )}
                  strokeWidth={2.5}
                />
                <span>{line}</span>
              </li>
            );
          })}
        </ul>
        <div
          aria-hidden="true"
          className="mt-8 h-0.5 overflow-hidden rounded-full bg-muted"
        >
          <div
            className={cn(
              "h-full rounded-full bg-primary ease-linear motion-safe:transition-[width] motion-safe:duration-[2200ms] motion-reduce:w-full",
              filled ? "w-full" : "w-0",
            )}
          />
        </div>
      </div>
    </>
  );
}
